import { type BaseMultiPageModel } from '../Models/Models/BaseMultiPageModel';

import { type MultiPageModelSnapshot, type Reason, type UndoRedoReason } from '../types';

export type UndoRedoSnapshot = {
    modelSnapshot: MultiPageModelSnapshot;
    timestamp: number;
};

const shouldStore = (args: (unknown | { reason: Reason })[]) => {
    if (args && args.length > 0 && typeof args[0] === 'object' && 'reason' in args[0]) {
        return !['undo', 'redo'].includes((args[0] as { reason: Reason }).reason);
    }

    return true;
};

export class UndoRedoManager {
    private creativeModel: BaseMultiPageModel;

    private cursor = 0;

    private stackCapacity = Infinity;

    private stack: UndoRedoSnapshot[] = [];

    private currentModelSnapshot: MultiPageModelSnapshot = null;

    private timer: ReturnType<typeof setTimeout> = null;

    private changeListener = (args: unknown[]) => shouldStore(args) && this.store();

    constructor(creativeModel: BaseMultiPageModel) {
        this.creativeModel = creativeModel;
        this.subscribe();
        this.currentModelSnapshot = this.creativeModel.getSnapshot();
    }

    private subscribe() {
        this.creativeModel.eventEmitter.on('elementCreated', this.changeListener);
        this.creativeModel.eventEmitter.on('elementUpdated', this.changeListener);
        this.creativeModel.eventEmitter.on('elementRemoved', this.changeListener);
        this.creativeModel.eventEmitter.on('backgroundColorUpdated', this.changeListener);
        this.creativeModel.eventEmitter.on('posterFrameUpdated', this.changeListener);
        this.creativeModel.eventEmitter.on('globalAudioUpdated', this.changeListener);
        this.creativeModel.eventEmitter.on('durationUpdated', this.changeListener);
        this.creativeModel.eventEmitter.on('dimensionUpdated', this.changeListener);
        this.creativeModel.eventEmitter.on('textStylesChanged', this.changeListener);
        this.creativeModel.eventEmitter.on('contentPropertyChanged', this.changeListener);
        this.creativeModel.eventEmitter.on('pageAdded', this.changeListener);
        this.creativeModel.eventEmitter.on('pageRemoved', this.changeListener);
        this.creativeModel.eventEmitter.on('pageRenamed', this.changeListener);
        this.creativeModel.eventEmitter.on('pageOrderChange', this.changeListener);
        this.creativeModel.eventEmitter.on('pagesMetadataUpdated', this.changeListener);
    }

    unsubscribe() {
        this.creativeModel.eventEmitter.off('elementCreated', this.changeListener);
        this.creativeModel.eventEmitter.off('elementUpdated', this.changeListener);
        this.creativeModel.eventEmitter.off('elementRemoved', this.changeListener);
        this.creativeModel.eventEmitter.off('backgroundColorUpdated', this.changeListener);
        this.creativeModel.eventEmitter.off('posterFrameUpdated', this.changeListener);
        this.creativeModel.eventEmitter.off('globalAudioUpdated', this.changeListener);
        this.creativeModel.eventEmitter.off('durationUpdated', this.changeListener);
        this.creativeModel.eventEmitter.off('dimensionUpdated', this.changeListener);
        this.creativeModel.eventEmitter.off('textStylesChanged', this.changeListener);
        this.creativeModel.eventEmitter.off('contentPropertyChanged', this.changeListener);
        this.creativeModel.eventEmitter.off('pageAdded', this.changeListener);
        this.creativeModel.eventEmitter.off('pageRemoved', this.changeListener);
        this.creativeModel.eventEmitter.off('pageRenamed', this.changeListener);
        this.creativeModel.eventEmitter.off('pageOrderChange', this.changeListener);
        this.creativeModel.eventEmitter.off('pagesMetadataUpdated', this.changeListener);
    }

    private flush() {
        if (!this.currentModelSnapshot) {
            return;
        }

        const snapshot: UndoRedoSnapshot = {
            modelSnapshot: this.currentModelSnapshot,
            timestamp: Date.now(),
        };
        this.currentModelSnapshot = null;

        this.stack[this.cursor] = snapshot;
        this.cursor++;

        if (this.stack.length !== this.cursor) {
            this.stack = this.stack.slice(0, this.cursor);
        }

        if (this.stack.length > this.stackCapacity) {
            this.stack.shift();
            this.cursor--;
        }
    }

    private store() {
        const delay = 400;
        this.flush();

        clearTimeout(this.timer);

        this.timer = setTimeout(() => {
            if (!this.currentModelSnapshot) {
                this.currentModelSnapshot = this.creativeModel.getSnapshot();
            }
        }, delay);

        this.creativeModel.eventEmitter.emit('undoRedoStateUpdate', { canUndo: true });
    }

    private restoreFromSnapshot(snapshot: UndoRedoSnapshot, reason: UndoRedoReason): void {
        const { modelSnapshot } = snapshot;

        const { cratedElements } = this.creativeModel.applySnapshot(modelSnapshot);

        this.currentModelSnapshot = this.creativeModel.getSnapshot();
        this.creativeModel.eventEmitter.emit('undoRedoStateUpdate', { canUndo: !!this.cursor });
        this.creativeModel.eventEmitter.emit('undoRedoSnapshotRestore', {
            reason,
            selectElementId: cratedElements?.[0]?.id || null,
        });
        this.creativeModel.eventEmitter.emit('requireCreativeUpdate', {});
        this.creativeModel.eventEmitter.emit('elementsTreeUpdated', {});
    }

    undo(): void {
        if (!this.cursor) {
            return;
        }

        if (this.stack.length === this.cursor) {
            if (!this.currentModelSnapshot) {
                this.currentModelSnapshot = this.creativeModel.getSnapshot();
            }

            this.flush();
            this.cursor--;
        }

        const snapshot = this.stack[--this.cursor];
        this.restoreFromSnapshot(snapshot, 'undo');
    }

    redo(): void {
        if (this.cursor >= this.stack.length - 1) {
            return;
        }

        const snapshot = this.stack[++this.cursor];
        this.restoreFromSnapshot(snapshot, 'redo');
    }
}
